import { Alert, Checkbox, Col, Row } from "antd"
import { CheckboxChangeEvent } from "antd/lib/checkbox"
import { first } from "lodash"
import { useCallback, useEffect, useState } from "react"
import { connect, useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { Button } from "components/Button"
import { Buttons, Card } from "components/Card/Card"
import PreviousButton from "components/Previous/PreviousButton"
import Consent from "containers/Consent/Consent"
import { PaymentCard } from "containers/Stripe"
import stripeActions from "containers/Stripe/services/actions"
import { PATH } from "containers/Survey/models"
import actionsSurvey from "containers/Survey/services/actions"
import { Answer } from "containers/Survey/types"
import { cdn } from "core/cdn"
import { CALL_PAGE, SURVEY_PAGE } from "core/constants"
import { request } from "lib/request"
import { Eta } from "types/payload"
import { AvailableDoctor, DoctorType, TreatingDoctorStatus } from "types/entity"
import { Dispatcher, Message } from "types/redux"
import { PaymentStore, SurveyStore } from "types/store"

import Loader from "components/Loaders/Loader/Loader"
import { logTerminalInformation } from "../../client/socket/actions"
import styles from "./Payment.module.scss"
import actions from "./services/actions"
import { useLanguage } from "locales"
import { GenericModal } from "components/Modal/Modal"
import { ContactlessPayment } from "containers/ContactlessPayment"
import { ContactlessPaymentState } from "containers/ContactlessPayment/services/reducer"
import { contactlessPaymentActions } from "containers/ContactlessPayment/services/actions"
import surveyActions from "containers/Survey/services/actions"

const enum PaymentState {
  INITIALIZE = "INITIALIZE",
  EXEMPTED = "EXEMPTED",
  PAYMENT = "PAYMENT",
}

type PaymentMethod = "card" | "contactlessPayment"

const selectIsReaderLinked = (state: { contactlessPayment: ContactlessPaymentState }): boolean =>
  Boolean(
    state.contactlessPayment?.readerLinked?.readers_ids &&
    state.contactlessPayment.readerLinked.readers_ids.length > 0 &&
    !state.contactlessPayment.readerLinked.loading
  )

const Payment = (props: PaymentStore) => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const tlcRequest = (a: Answer[]) => { dispatch(actions.submit(a)) };
  const [paymentState, setPaymentState] = useState(PaymentState.INITIALIZE)
  const [exempted, setExempted] = useState(false)
  const [consentVisibility, setConsentVisibility] = useState(false)
  let answers = useSelector(({ survey }) => survey.answers)

  const [consentChoice, setConsentChoice] = useState<boolean>(false)
  const { hasPaymentError: stripeError, hasSetupIntentError } = useSelector((state: any) => state.stripe)
  const email = useSelector(({client}) => client?.customer?.email )
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
    const { readerLinked } = useSelector(
    (state: any) => state.contactlessPayment
  )
  const isReaderLinked = useSelector(selectIsReaderLinked)
  const [openModalAfterCheck, setOpenModalAfterCheck] = useState(false)
  const [eta, setEta] = useState<string | undefined>(undefined)

  useEffect(() => {
    request<{ data: Eta }>("/calls/eta", { method: "GET" })
      .then((res) => {
        if (res?.data?.displayed) setEta(res.data.displayed)
      })
      .catch((err) => console.error(err, { route: "/calls/eta" }))
  }, [])

  const paymentIntentClientSecret = useSelector(
    ({ stripe }: { stripe: any }) =>
      stripe?.paymentIntent?.paymentintent?.client_secret,
  )
  const publicStripeKey = useSelector(
    ({ stripe }: { stripe: any }) => stripe?.public_stripe_key,
  )

  // Opening the modal before the reader check and the intent settle makes its content swap mid-load
  useEffect(() => {
    if (!openModalAfterCheck) return
    if (readerLinked.loading) return
    if (!paymentIntentClientSecret && !hasSetupIntentError) return

    setOpenModalAfterCheck(false)
    if (hasSetupIntentError) return
    if (readerLinked.error) console.error(readerLinked.error)

    setPaymentMethod(isReaderLinked ? null : "card")
    setIsPreparingCard(false)
    setPaymentModalVisible(true)
  }, [
    openModalAfterCheck,
    readerLinked.loading,
    readerLinked.error,
    isReaderLinked,
    paymentIntentClientSecret,
    hasSetupIntentError,
  ])

  useEffect(() => {
    dispatch(actions.getCallPrice())
    if (!publicStripeKey) dispatch(stripeActions.getTokenRequest())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if(stripeError) {
      setPaymentModalVisible(false);
      setIsPreparingCard(false)
    }
  }, [stripeError])


  useEffect(() => {
    if(paymentMethod === 'card' && isReaderLinked) {
      // Ne recrée pas l'intent si on l'a déjà (sinon on provoque des remounts Elements)
      if (!paymentIntentClientSecret) dispatch(actions.createPaymentIntent());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, isReaderLinked, paymentIntentClientSecret])

  const [dataConsentChoice, setDataConsentChoice] = useState<boolean>(false)
  const [initialModalCheck, setInitialModalCheck] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [isPreparingCard, setIsPreparingCard] = useState(false);
  const [setUpIntentLoading, setSetupIntentLoading] = useState<boolean>(false);
  const { payment_intent_id, status: contactlessPaymentStatus } = useSelector(({contactlessPayment}) => contactlessPayment)
  const treatingDoctorStatus = useSelector(({ survey }: { survey: SurveyStore }) => survey.answers.find((a) => a.questionId === PATH.doctor_available)?.value[0]) as number
  

  useEffect(() => {
    if(hasSetupIntentError) {
      setSetupIntentLoading(false)
    }
  }, [hasSetupIntentError]);

  // Quand l'intent Stripe est prêt, on bascule vers l'écran carte.
  useEffect(() => {
    if (
      paymentModalVisible &&
      isReaderLinked &&
      isPreparingCard &&
      paymentIntentClientSecret
    ) {
      setPaymentMethod("card")
      setIsPreparingCard(false)
    }
  }, [paymentModalVisible, isReaderLinked, isPreparingCard, paymentIntentClientSecret])

  const handleContactlessPaymentSuccess = useCallback(() => {
    if (payment_intent_id) {
      const contactlessPaymentAnswer: Answer[] = [
        {
          questionId: "payment_sheet_intent_id",
          value: [payment_intent_id]
        }
      ]
      const send = [...answers, ...contactlessPaymentAnswer]
      tlcRequest(send)
    }
    setPaymentModalVisible(false)
    setPaymentMethod(null)
    setIsPreparingCard(false)
  }, [payment_intent_id, answers, tlcRequest])

  const handleContactlessPaymentCancel = useCallback(() => {
    // Fermer la modale immédiatement pour éviter l'affichage de l'état cancelled
    setPaymentModalVisible(false)
    setPaymentMethod(null)
    setIsPreparingCard(false)

    if (contactlessPaymentStatus === "creating" || contactlessPaymentStatus === "polling") {
      dispatch(contactlessPaymentActions.pollCancel())
      dispatch(contactlessPaymentActions.reset())
    }
  }, [contactlessPaymentStatus, dispatch])
  
  const handleSwitchToCard = useCallback(() => {
    // Si un paiement est en cours, on annule le paiement mobile
    if (contactlessPaymentStatus === "creating" || contactlessPaymentStatus === "polling") {
      dispatch(contactlessPaymentActions.pollCancel())
    }
    // Basculer vers le paiement par carte, mais seulement quand l'intent Stripe est prêt
    if (paymentIntentClientSecret) {
      setPaymentMethod("card")
      setIsPreparingCard(false)
      return
    }

    setPaymentMethod(null)
    setIsPreparingCard(true)
    dispatch(actions.createPaymentIntent())
  }, [contactlessPaymentStatus, dispatch, paymentIntentClientSecret])
  
  useEffect(() => {
    // Retrieve terminal information and send them to API
    if (props.success) {
      dispatch(logTerminalInformation())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.success])
  // réception des réponses du questionnaire
  useEffect(() => {
    if (!props.answers) {
      // Nous n'avons pas accès aux réponses du questionnaire
      // Le patient ne peux pas lancer de TLC
      navigate(SURVEY_PAGE)
    } else {
      // Nous vérifions l'état de son exonération
      // Par défaut, false, puis nous mettons à jour l'état de la page
      // exmption ? PaymentState.EXEMPTED : PaymentState.PAYMENT
      const exempted = props.answers.find(
        (answer: Answer) =>
          answer.questionId === PATH.exemption &&
          Array.isArray(answer.value) &&
          first(answer.value) === true
      )
      const hasSelectedDoctorReply = props.answers.find(
        (answer: Answer) =>
          answer.questionId === PATH.choice_of_doctor &&
          Array.isArray(answer.value) &&
          typeof first(answer.value) === "object"
          && first(answer.value) !== null
      )
      // si un docteur est selectionné et que le patient n'est pas déjà exempté
      if (hasSelectedDoctorReply && !exempted) {
        const chosenDoctor = first(hasSelectedDoctorReply?.value) as
          | AvailableDoctor
          | undefined
        if (chosenDoctor?.category_id === DoctorType.LABOUR_DOCTOR)
          setExempted(true)
        else {
          // Si le patient a choisi un docteur qui n'est pas
          // médecin du travail, on passe par la page de paiement
          setExempted(false)
        }
      } else setExempted(!!exempted)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.answers])

  useEffect(() => {
    if (props.success) {
      navigate(CALL_PAGE)
      props.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.success])

  switch (paymentState) {
    case PaymentState.PAYMENT:
    default:
      return (
        <Card
          titleSize="small"
          title={languages.youWillLaunchATLC}
          message={props.message}
          style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '25px', margin: 0}}
        >
          <PreviousButton
            onClick={() => {
              setPaymentState(PaymentState.INITIALIZE)
            }}
            to={SURVEY_PAGE}
          />
          <div className={styles.PaymentInformation}>
            <Row align="middle" justify="center" gutter={[10, 10]}>
              <Col span={4}>
                <img src={cdn("/images/paiement_picto.svg")} alt="" />
              </Col>
              <Col span={20}>
              {
                hasSetupIntentError ? <Alert type="error" className={styles.AlertError} showIcon message={<span style={{ color: '#D1293A', fontSize: '28px', lineHeight: '40px' }}>{languages.createPaymentError}</span>} description={<><p style={{ color: '#D1293A', fontSize: '18px', lineHeight: '25px', fontWeight: 'unset' }}>{languages.retryLater}  <span>{languages.ifErrorStillOccur}</span></p><Button type="primary" wide="short" size="xsmall" onClick={() => { setSetupIntentLoading(true); dispatch({ type: "SETUP_PAYMENT_INTENT_REQUEST" }) }}>{languages.retry}</Button> </>} />
                  :
                  props?.price?.price && (
                    <span className={styles.PaymentInformationText}>
                      {languages.price} : <b>{props?.price?.price} €</b>{" "}
                      <i>{props?.price?.content}</i>
                    </span>
                  )
              }
              </Col>
            </Row>
          </div>
          <Consent
              checked={initialModalCheck}
              onConsentChange={(isAllChecked) => {
                setDataConsentChoice(isAllChecked)}
              }
              visible={consentVisibility}
              onCancel={() => {
                setConsentVisibility(false)
              }}
              onAccept={() => {
                setConsentChoice(true)
                setConsentVisibility(false)
              }}
            ></Consent>
            <div style={{ width: '900px', paddingTop: '50px', padding: '20px 50px', margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Row>
                <Checkbox
                  checked={consentChoice}
                  onChange={(event: CheckboxChangeEvent) => {
                    setConsentChoice(event.target.checked)
                  }}
                >
                  {languages.IConfirmMyConsent}
                </Checkbox>
              </Row>
              <Row>
                <Checkbox
                  checked={dataConsentChoice}
                  onChange={(event: CheckboxChangeEvent) => {
                    setDataConsentChoice(event.target.checked);
                    setInitialModalCheck(event.target.checked);
                    if(event.target.checked === true && treatingDoctorStatus === TreatingDoctorStatus.DOCTOR_AWAY_NO_CONSENT) {
                      dispatch(surveyActions.sendReportToMTConsent(true))
                    }
                  }}
                >
                  {languages.IConfirmDataConsent}
                  <span
                    className="more-information"
                    onClick={(ev) => {
                      ev.preventDefault()
                      setConsentVisibility(true)
                    }}
                  >
                    {" "}
                    {languages.personalize}
                  </span>
                </Checkbox>
              </Row>
            <Button
              loading={(props.loading || openModalAfterCheck) && !hasSetupIntentError}
              disabled={!consentChoice || !dataConsentChoice || props.loading || openModalAfterCheck || hasSetupIntentError}
              type="primary"
              style={{ marginTop: '50px' }}
              onClick={() => {
                // Si le prix de la TLC est à 0, hors exemption
                if(props.price?.price === "0") {
                  tlcRequest(answers)
                  return;
                }
                dispatch(stripeActions.clearPaymentIntent())
                dispatch(actions.createPaymentIntent())
                dispatch(contactlessPaymentActions.readerCheckLinkedRequest())
                setOpenModalAfterCheck(true)
              }}
            >
              {props.price?.price === "0" ? "lancer la TLC" : languages.payAndLaunchTLC}
            </Button>
            {eta && (
              <span
                style={{
                  marginTop: "20px",
                  color: "#6E6D7A",
                  fontSize: "22px",
                  textAlign: "center",
                }}
              >
                {languages.estimatedTlcWaitingTime}
                {eta}
              </span>
            )}
            </div>
          <GenericModal visible={paymentModalVisible} footer={<></>} style={{ maxWidth: '1500px', maxHeight: '800px' }} closable onClose={() => {
            // Si on ferme la modal pendant un paiement mobile, on annule le paiement
            if (paymentMethod === "contactlessPayment") {
              handleContactlessPaymentCancel()
            } else {
              setPaymentModalVisible(false)
              setPaymentMethod(null)
              setIsPreparingCard(false)
            }
          }}>
            {!isReaderLinked ? (
              <PaymentCard
                onPay={(pay: Answer[]) => {
                  const send = [...answers, ...pay]
                  tlcRequest(send)
                  setPaymentModalVisible(false)
                  setPaymentMethod(null)
                }}
              />
            ) : !paymentMethod ? (
              <div style={{
                display: 'flex',
                width: '1304px !important',
                height: '616px !important',
                padding: '60px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '66px',
                flexShrink: '0',
              }}>
                {isPreparingCard ? (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Loader size="140px" />
                  </div>
                ) : (
                  <>
                    <div style={{
                      color: '#211452',
                      textAlign: 'center',
                      fontFamily: 'Now',
                      fontSize: '48px',
                      fontWeight: '700',
                    }}>
                      {languages.choosePaymentMethod}
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '66px',
                      width: '100%',
                      maxWidth: '1100px',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <div
                        className={styles.paymentChoice}
                        onClick={() => {
                          if (isPreparingCard) return
                          // On lance la création côté backend, mais on bascule vers PaymentCard uniquement
                          // quand Stripe renvoie un client_secret (anti "blink").
                          if (paymentIntentClientSecret) {
                            setPaymentMethod("card")
                            setIsPreparingCard(false)
                            return
                          }
                          setIsPreparingCard(true)
                          dispatch(actions.createPaymentIntent())
                        }}
                      >
                        <img
                          src={cdn("/icons/payment/account_cards.svg")}
                          alt="Carte bancaire"
                          style={{ width: '120px', height: '120px' }}
                        />
                        <div style={{
                          color: '#211452',
                          textAlign: 'center',
                          fontFamily: 'Now',
                          fontSize: '28px',
                          fontWeight: '400',
                        }}>
                          {languages.payByCard}
                        </div>
                      </div>
                      <div
                        onClick={() => {
                          if (isPreparingCard) return
                          setPaymentMethod("contactlessPayment")
                          setIsPreparingCard(false)
                        }}
                      >
                        <div style={{
                          color: '#1D71B8',
                          textAlign: 'center',
                          fontFamily: 'Now',
                          fontSize: '28px',
                          fontWeight: '400',
                          textDecoration: 'underline',
                        }}>
                          {languages.payByContactlessPayment}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : paymentMethod === "card" ? (
              <PaymentCard
                onPay={(pay: Answer[]) => {
                  const send = [...answers, ...pay]
                  tlcRequest(send)
                  setPaymentModalVisible(false)
                  setPaymentMethod(null)
                  setIsPreparingCard(false)
                }}
              />
            ) : (
              <ContactlessPayment
                onSuccess={handleContactlessPaymentSuccess}
                onSwitchToCard={handleSwitchToCard}
              />
            )}
          </GenericModal>
        </Card>
      )
    case PaymentState.INITIALIZE:
    case PaymentState.EXEMPTED:
      return (
        <Card
          title={languages.putHeadPhones}
          subtitle={!exempted && languages.nextMeansYouAcceptCGU}
          imgUrl={cdn("/images/casque_illustration.svg")}
          message={props.message}
        >
          <PreviousButton
            onClick={() => {
              props.reset()
              dispatch(actionsSurvey.previous())
            }}
            to={SURVEY_PAGE}
          />
          {exempted ? (
            <Buttons direction="vertical">
              <div style={{ marginTop: "-80px"}}>
                <Checkbox
                  checked={consentChoice}
                  onChange={(event: CheckboxChangeEvent) => {
                    setConsentChoice(event.target.checked)
                  }}
                >
                  {languages.IConfirmMyConsent}
                </Checkbox>
                <Checkbox checked={dataConsentChoice} onChange={(e) => {
                  setDataConsentChoice(e.target.checked)
                  setInitialModalCheck(e.target.checked)
                }}>
                  {languages.IConfirmDataConsent}
                  <span className="more-information" onClick={(ev) => {
                    ev.preventDefault();
                    setConsentVisibility(true);
                  }}
                  >
                    {" "}
                    {languages.personalize}
                  </span>
                </Checkbox>
              </div>
            </Buttons>
          ) : null}
          <Buttons>
            <Button
              type="primary"
              disabled={exempted ? !consentChoice || !dataConsentChoice : props.loading}
              loading={props.loading}
              onClick={() => {
                exempted && tlcRequest(answers)
                !exempted && setPaymentState(PaymentState.PAYMENT)
              }}
            >
              {exempted ? languages.createCall : languages.next}
            </Button>
          </Buttons>
          <Consent
            checked={initialModalCheck}
            onConsentChange={(isAllChecked) => setDataConsentChoice(isAllChecked)}
            visible={consentVisibility}
            onCancel={() => {
              setConsentVisibility(false)
            }}
            onAccept={() => {
              setConsentChoice(true)
              setDataConsentChoice(true)
              setConsentVisibility(false)
            }}
          ></Consent>
        </Card>
      )
  }
}

const mapDispatchToProps = (dispatch: Dispatcher): Partial<PaymentStore> => {
  return {
    reset: () => dispatch(actions.reset()),
  }
}
// Give the store inside the props
export default connect(
  ({
    payment,
    stripe,
  }: {
    payment: PaymentStore
    stripe: { message?: Message }
  }) => {
    return {
      ...payment,
      message: payment.message ?? stripe.message,
    }
  },
  mapDispatchToProps
)(Payment)
